import type { FinancialAssignmentResponse, FinancialShipmentResponse } from '@/types/Api/FinancialShipment'
import type { AxiosCollection } from '@/types/Components/Response'
import type { CostMarginsResponse } from '@/types/Models/CostMargin'
import { BaseProxy } from '@/proxies/Proxy'

export default class FinancialProxy extends BaseProxy {
  public endpoint = 'shipments'

  findShipments(
    numbers: string[],
    page: number,
    signal: AbortSignal,
  ): Promise<AxiosCollection<FinancialShipmentResponse>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: '100',
      exclude: 'recipient,externalTracking,servicePoints',
    })
    numbers.forEach((number) => params.append('shipmentNumber[]', number))
    return this.submit('get', `/shipments?${params}`, null, { signal })
  }

  // Existing assignment list: one request per page of a company's assignments, only for their sender address.
  findAssignments(
    companyId: string,
    dateFrom: string,
    dateTo: string,
    page: number,
    signal: AbortSignal,
  ): Promise<AxiosCollection<FinancialAssignmentResponse>> {
    const params = new URLSearchParams({
      companyId,
      dateFrom,
      dateTo,
      page: String(page),
      limit: '100',
      exclude: 'company,item,recipient,sourceFiles',
    })
    return this.submit('get', `/assignments?${params}`, null, { signal })
  }

  // Everything PacSort shipped in a booking period for the given carriers: the base of the Financial ledger.
  listShipments(
    dateFrom: string,
    dateTo: string,
    carrierHandles: string[],
    page: number,
    signal: AbortSignal,
  ): Promise<AxiosCollection<FinancialShipmentResponse>> {
    const params = new URLSearchParams({
      dateFrom,
      dateTo,
      page: String(page),
      limit: '100',
      exclude: 'recipient,externalTracking,servicePoints',
    })
    carrierHandles.forEach((handle) => params.append('carrierHandle[]', handle))
    return this.submit('get', `/shipments?${params}`, null, { signal })
  }

  // The customer's margin per carrier (Customers › Cost margins), with the system default for carriers without one.
  findCostMargins(companyId: string, signal: AbortSignal): Promise<CostMarginsResponse> {
    return this.submit('get', `/companies/${encodeURIComponent(companyId)}/cost-margins`, null, { signal })
  }
}
